import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PlusCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import AnnouncementForm from "./components/AnnouncementForm";
import AnnouncementList from "./components/AnnouncementList";
import { axiosInstance } from "@/pages/auth/auth.context";
import { parseLocalDate } from "@/lib/utils";

export interface Announcement {
  id: number;
  title: string;
  content: string;
  startDate: string;
  endDate: string;
  emailToEmployees: boolean;
}

export default function ManageAnnouncements() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined,
    emailOption: "no",
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const { toast } = useToast();

  // Fetch announcements on component mount
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_API_URL}announcement/all`,
      );
      setAnnouncements(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch announcements",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      startDate: undefined,
      endDate: undefined,
      emailOption: "no",
    });
    setEditingId(null);
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const newAnnouncement = {
        title: formData.title,
        content: formData.content,
        startDate: formData.startDate?.toISOString().split("T")[0] || "",
        endDate: formData.endDate?.toISOString().split("T")[0] || "",
        emailToEmployees: formData.emailOption === "yes",
      };

      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_API_URL}announcement`,
        newAnnouncement,
      );

      toast({
        title: "Announcement Created",
        description: "The announcement has been created successfully.",
      });

      setIsCreateOpen(false);
      resetForm();
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create announcement",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setFormData({
      title: announcement.title,
      content: announcement.content,
      startDate: parseLocalDate(announcement.startDate),
      endDate: parseLocalDate(announcement.endDate),
      emailOption: announcement.emailToEmployees ? "yes" : "no",
    });
    setEditingId(announcement.id);
    setIsEditOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditOpen(false);
    resetForm();
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;

    try {
      const updatedAnnouncement = {
        title: formData.title,
        content: formData.content,
        startDate: formData.startDate?.toISOString().split("T")[0] || "",
        endDate: formData.endDate?.toISOString().split("T")[0] || "",
        emailToEmployees: formData.emailOption === "yes",
      };

      await axiosInstance.put(
        `${process.env.REACT_APP_BASE_API_URL}announcement/${editingId}`,
        updatedAnnouncement,
      );

      toast({
        title: "Announcement Updated",
        description: "The announcement has been updated successfully.",
      });

      handleEditDialogClose();
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update announcement",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_BASE_API_URL}announcement/${id}`,
      );

      toast({
        title: "Announcement Deleted",
        description: "The announcement has been deleted successfully.",
      });

      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete announcement",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-foreground">
          Special Announcements
        </h1>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <PlusCircle className="mr-2 h-4 w-4" />
              Create Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[625px]">
            <DialogHeader>
              <DialogTitle>Create New Announcement</DialogTitle>
            </DialogHeader>
            <AnnouncementForm
              onSubmit={handleCreate}
              title={formData.title}
              setTitle={(title) => setFormData((prev) => ({ ...prev, title }))}
              content={formData.content}
              setContent={(content) =>
                setFormData((prev) => ({ ...prev, content }))
              }
              startDate={formData.startDate}
              setStartDate={(date) =>
                setFormData((prev) => ({ ...prev, startDate: date }))
              }
              endDate={formData.endDate}
              setEndDate={(date) =>
                setFormData((prev) => ({ ...prev, endDate: date }))
              }
              emailOption={formData.emailOption}
              setEmailOption={(option) =>
                setFormData((prev) => ({ ...prev, emailOption: option }))
              }
              submitLabel="Create Announcement"
            />
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={isEditOpen} onOpenChange={handleEditDialogClose}>
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
          </DialogHeader>
          <AnnouncementForm
            onSubmit={handleUpdate}
            title={formData.title}
            setTitle={(title) => setFormData((prev) => ({ ...prev, title }))}
            content={formData.content}
            setContent={(content) =>
              setFormData((prev) => ({ ...prev, content }))
            }
            startDate={formData.startDate}
            setStartDate={(date) =>
              setFormData((prev) => ({ ...prev, startDate: date }))
            }
            endDate={formData.endDate}
            setEndDate={(date) =>
              setFormData((prev) => ({ ...prev, endDate: date }))
            }
            emailOption={formData.emailOption}
            setEmailOption={(option) =>
              setFormData((prev) => ({ ...prev, emailOption: option }))
            }
            submitLabel="Update Announcement"
          />
        </DialogContent>
      </Dialog>

      <AnnouncementList
        announcements={announcements}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
}
